import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { from, throwError, EMPTY } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // ❗️Evitar interceptar la solicitud de refresh
  if (req.url.includes('/oauth2/token')) {
    return next(req);
  }

  const accessToken = localStorage.getItem('accessToken');

  if (!accessToken) return next(req);

  // Función para procesar request con manejo de errores HTTP
  const processRequestWithErrorHandling = (token: string) => {
    return next(
      req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      })
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        // Manejar errores de autenticación/autorización
        if (error.status === 401 || error.status === 403) {
          console.log('Token rejected by server, attempting refresh...', error.status);
          
          // Evitar bucles infinitos - verificar si ya se intentó refresh
          if (req.headers.has('X-Token-Refresh-Attempted')) {
            console.error('Refresh failed, logging out');
            alert('Your session has expired. You will be redirected to login.');
            authService.logout();
            return EMPTY;
          }
          
          // Intentar refresh del token
          return from(authService.refreshSession()).pipe(
            switchMap(() => {
              const newToken = localStorage.getItem('accessToken');
              if (!newToken) {
                alert('Your session has expired. You will be redirected to login.');
                authService.logout();
                return EMPTY;
              }
              
              // Reintentar la request original con el nuevo token
              return next(
                req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                    'X-Token-Refresh-Attempted': 'true'
                  },
                })
              );
            }),
            catchError((refreshError) => {
              console.error('Error refreshing token after HTTP error:', refreshError);
              alert('Your session has expired. You will be redirected to login.');
              authService.logout();
              return EMPTY;
            })
          );
        }
        
        // Para otros errores, propagarlos normalmente
        return throwError(() => error);
      })
    );
  };

  try {
    const { exp } = JSON.parse(atob(accessToken.split('.')[1]));
    const now = Date.now();
    if (now >= exp * 1000) {
      // Token expirado → refrescar
      return from(authService.refreshSession()).pipe(
        switchMap(() => {
          const newToken = localStorage.getItem('accessToken');
          return processRequestWithErrorHandling(newToken || accessToken);
        })
      );
    } else {
      // Token válido - usar directamente
      return processRequestWithErrorHandling(accessToken);
    }
  } catch (e) {
    console.error('Malformed token, sending request and handling HTTP error:', e);
    // Si el token está malformado, enviarlo igual y que el servidor responda 401
    return processRequestWithErrorHandling(accessToken);
  }
};
